import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Check,
  ChevronRight,
  Clock3,
  Heart,
  Home,
  Leaf,
  MapPin,
  PackageCheck,
  Scale,
  ShieldCheck,
  ShoppingCart,
  Tag,
  Truck,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import DecorLeaf from '../components/DecorLeaf';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import QuantityStepper from '../components/QuantityStepper';
import CatalogProductTile, { CATALOG_GRID, CatalogTileSkeleton } from '../components/CatalogProductTile';
import ProductImage from '../components/ProductImage';
import usePageMetadata from '../hooks/usePageMetadata';
import * as catalogApi from '../api/endpoints/catalog';
import { isStaleLocationError } from '../utils/staleLocation';
import { formatOfferDate, formatRupees, productPriceView } from '../utils/offerPresentation';
import { displayLabelFor } from '../utils/deliveryLocationLabels';
import { useCart } from '../context/CartContext';
import { useToast } from '../context/ToastContext';
import { useDeliveryLocation } from '../context/DeliveryLocationContext';
import { FAVOURITE_AUTH_REQUIRED, useFavourites } from '../context/FavouritesContext';
import { focusRing, homeRail } from '../styles/ui';

// Brand promises already published elsewhere on the site. Nothing product-
// specific (no ratings, purchase counts, nutrition or cooking advice) is
// invented for a product the backend does not describe that way.
const BENEFITS = [
  { icon: Leaf, title: 'Farm Fresh', subtitle: 'Direct from farms' },
  { icon: PackageCheck, title: 'Hygienically Packed', subtitle: "For your family's safety" },
  { icon: BadgeCheck, title: 'Quality Checked', subtitle: 'Inspected before dispatch' },
];
const WHY_US = [
  'Farm fresh, handpicked daily',
  'Hygienically cleaned & packed',
  'Quality checked before dispatch',
  'Same-day delivery to your door',
];

const ProductDetailPage = () => {
  const { idOrSku } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'loading', data: null, error: null });
  const [related, setRelated] = useState({ status: 'idle', products: [] });
  const [activeImage, setActiveImage] = useState(0);
  // Only the latest product request may write state: a slow response for a
  // previous product (or a previous delivery location) is dropped.
  const requestIdRef = useRef(0);
  const { addItem, setQuantity, quantityFor } = useCart();
  const { isFavourite, toggleFavourite } = useFavourites();
  const { showToast } = useToast();
  const { label: locationLabel, pincode: locationPincode, status: locationStatus, openLocationPrompt } = useDeliveryLocation();

  const product = state.data;
  // Same backend-projected pricing and offer as the product cards.
  const pricing = productPriceView(product);
  const favourited = product ? isFavourite(product._id) : false;

  const handleToggleFavourite = async () => {
    if (!product) return;
    try {
      const wasFavourited = favourited;
      const result = await toggleFavourite(product._id, product);
      if (result === FAVOURITE_AUTH_REQUIRED) {
        const redirect = `${location.pathname}${location.search}${location.hash}`;
        navigate(`/login?redirect=${encodeURIComponent(redirect)}`);
        return;
      }
      showToast(wasFavourited ? 'Removed from favourites' : 'Added to favourites');
    } catch {
      // Best-effort — FavouritesContext already rolls back the optimistic
      // update on failure, nothing further to surface here.
    }
  };

  const handleAddToCart = () => {
    addItem(product);
    showToast(`${product.displayName} added to cart`);
  };

  const handleBuyNow = () => {
    addItem(product);
    navigate('/cart');
  };

  usePageMetadata({
    title: `${product?.displayName || 'Product'} | Fresh & Homely`,
    description: product?.shortDescription || 'Shop fresh products at Fresh & Homely.',
    canonicalPath: `/products/${idOrSku}`,
  });

  const load = () => {
    const requestId = ++requestIdRef.current;
    setState({ status: 'loading', data: null, error: null });
    setRelated({ status: 'idle', products: [] });
    setActiveImage(0);
    catalogApi
      .getProduct(idOrSku)
      .then((response) => {
        if (requestId === requestIdRef.current) setState({ status: 'success', data: response.data, error: null });
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current || isStaleLocationError(err)) return;
        // The catalogue is scoped to the delivery location, so a 404 usually
        // means the store delivering here does not sell this product.
        const message = err.status === 404
          ? "This product isn't available at your delivery location."
          : err.message;
        setState({ status: 'error', data: null, error: message });
      });
  };

  useEffect(() => {
    load();
    return () => {
      requestIdRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idOrSku]);

  // Related products are simply other items in the same category — there is
  // no dedicated "related products" concept in the backend, so this reuses
  // the existing category listing endpoint rather than inventing new data.
  useEffect(() => {
    if (!product?.category) {
      setRelated({ status: 'idle', products: [] });
      return undefined;
    }
    let active = true;
    setRelated({ status: 'loading', products: [] });
    catalogApi
      .listProducts({ category: product.category, limit: 8 })
      .then((response) => {
        if (!active) return;
        const items = (response.data || []).filter((item) => item._id !== product._id).slice(0, 6);
        setRelated({ status: 'success', products: items });
      })
      .catch(() => {
        if (active) setRelated({ status: 'error', products: [] });
      });
    return () => {
      active = false;
    };
  }, [product?.category, product?._id]);

  // The real gallery: the thumbnail first, then any extra images the
  // projection carries. No placeholder frames are invented.
  const images = product
    ? [product.thumbnail, ...(Array.isArray(product.gallery) ? product.gallery : [])].filter(
        (src, index, all) => Boolean(src) && all.indexOf(src) === index
      )
    : [];
  const mainImage = images[activeImage] || images[0] || null;
  const quantity = product ? quantityFor(product._id) : 0;
  // Related cards share the listing card and its existing favourite flow.
  const handleToggleRelatedFavourite = async (item) => {
    try {
      const wasFavourited = isFavourite(item._id);
      const result = await toggleFavourite(item._id, item);
      if (result === FAVOURITE_AUTH_REQUIRED) {
        navigate(`/login?redirect=${encodeURIComponent(`${location.pathname}${location.search}`)}`);
        return;
      }
      showToast(wasFavourited ? 'Removed from favourites' : 'Added to favourites');
    } catch {
      // FavouritesContext rolls back its optimistic update on failure.
    }
  };
  const handleAddRelated = (item) => {
    addItem(item);
    showToast(`${item.displayName} added to cart`);
  };

  const card = 'bg-white rounded-[18px] border border-[#E4EDE4] shadow-[0_8px_22px_-18px_rgba(15,51,24,0.3)]';

  return (
    <>
      <Navbar />
      <main className="relative overflow-hidden pt-20 bg-[linear-gradient(180deg,#F7FBF5_0%,#FFFFFF_45%,#F7FBF5_100%)]">
        <div className="pointer-events-none select-none absolute inset-0" aria-hidden="true">
          <DecorLeaf className="hidden xl:block absolute left-[50px] top-[100px] w-9 text-emerald-500/70 rotate-[20deg]" />
          <DecorLeaf className="hidden xl:block absolute left-[40px] top-[380px] w-7 text-emerald-600/60 -rotate-[26deg]" />
          <DecorLeaf className="hidden xl:block absolute right-[60px] top-[120px] w-10 text-emerald-500/70 -rotate-[16deg]" />
          <DecorLeaf className="hidden xl:block absolute right-[40px] top-[420px] w-8 text-emerald-600/60 rotate-[30deg]" />
        </div>

        <div className={`${homeRail} relative pt-[16px] pb-12`}>
          <nav aria-label="Breadcrumb" className="xl:px-[30px]">
            <ol className="flex flex-wrap items-center gap-1.5 text-[12.5px] text-ink-secondary">
              <li>
                <Link to="/" className={`inline-flex items-center gap-1.5 hover:text-emerald-700 ${focusRing}`}>
                  <Home size={13} aria-hidden="true" />
                  Home
                </Link>
              </li>
              <li aria-hidden="true"><ChevronRight size={13} /></li>
              <li>
                <Link to="/categories" className={`hover:text-emerald-700 ${focusRing}`}>Categories</Link>
              </li>
              {product?.category && (
                <>
                  <li aria-hidden="true"><ChevronRight size={13} /></li>
                  <li>
                    <Link to={`/categories/${product.category}`} className={`hover:text-emerald-700 ${focusRing}`}>
                      {product.categoryLabel || product.category}
                    </Link>
                  </li>
                </>
              )}
              {product && (
                <>
                  <li aria-hidden="true"><ChevronRight size={13} /></li>
                  <li aria-current="page" className="font-medium text-emerald-800">{product.displayName}</li>
                </>
              )}
            </ol>
          </nav>

          {state.status === 'loading' && <LoadingState label="Loading product..." />}
          {state.status === 'error' && (
            <div className="mt-6">
              <ErrorState message={state.error} onRetry={load} />
              <Link to="/categories" className={`mt-4 inline-flex items-center gap-2 text-sm font-semibold text-emerald-800 ${focusRing}`}>
                <ArrowLeft size={16} aria-hidden="true" />
                Browse categories
              </Link>
            </div>
          )}

          {state.status === 'success' && (
            // Two columns, two rows. The right column spans both rows, and the
            // second row is the flexible one, so Related Products starts right
            // after the main content instead of waiting for the taller aside.
            <div className="mt-[14px] grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] lg:grid-rows-[auto_1fr] gap-5 xl:gap-6 items-start xl:px-[7px]">
              <div className="min-w-0 lg:col-start-1 lg:row-start-1">
                <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] xl:grid-cols-[minmax(0,470px)_minmax(0,1fr)] gap-6 xl:gap-8 items-start">
                  {/* Gallery */}
                  <div className="flex gap-4">
                    {images.length > 1 && (
                      <ul className="flex flex-col gap-2.5 shrink-0" aria-label="Product images">
                        {images.map((src, index) => (
                          <li key={src}>
                            <button
                              type="button"
                              onClick={() => setActiveImage(index)}
                              aria-label={`Show image ${index + 1}`}
                              aria-pressed={index === activeImage}
                              className={`block w-[62px] h-[62px] rounded-xl overflow-hidden border-2 ${
                                index === activeImage ? 'border-[#2E8B47]' : 'border-transparent'
                              } ${focusRing}`}
                            >
                              <ProductImage src={src} alt="" size="thumb" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="relative flex-1 min-w-0 aspect-[4/3] rounded-[20px] overflow-hidden bg-[#F4F7F3] border border-[#E4EDE4] shadow-[0_10px_30px_-18px_rgba(15,51,24,0.35)]">
                      <ProductImage src={mainImage} alt={product.displayName} loading="eager" />
                      {pricing.discountPercentage && (
                        <span className="absolute top-4 left-4 bg-[#F4642C] text-white text-[13px] font-bold px-3.5 py-1.5 rounded-full shadow-[0_3px_8px_-2px_rgba(244,100,44,0.6)]">
                          {pricing.discountPercentage}% OFF
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={handleToggleFavourite}
                        aria-label={favourited ? 'Remove from favourites' : 'Add to favourites'}
                        aria-pressed={favourited}
                        className={`absolute top-4 right-4 w-10 h-10 rounded-full bg-white shadow-card flex items-center justify-center transition-transform hover:scale-105 ${focusRing}`}
                      >
                        <Heart size={18} className={favourited ? 'text-accent-500 fill-accent-500' : 'text-emerald-950'} aria-hidden="true" />
                      </button>
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="min-w-0">
                    {product.categoryLabel && (
                      <Link
                        to={`/categories/${product.category}`}
                        className={`inline-flex items-center h-6 px-3 rounded-full bg-[#E4F3E5] text-emerald-800 text-[11.5px] font-semibold ${focusRing}`}
                      >
                        {product.categoryLabel}
                      </Link>
                    )}
                    <h1 className="serif mt-[10px] text-[28px] sm:text-[34px] font-bold text-emerald-950 leading-[1.08]">{product.displayName}</h1>
                    {product.shortDescription && (
                      <p className="mt-[6px] text-[14.5px] text-[#4B5563] leading-[1.4]">{product.shortDescription}</p>
                    )}

                    <div className="mt-[14px] flex flex-wrap items-center gap-x-3 gap-y-2">
                      <span className="text-[30px] font-extrabold text-emerald-950 leading-none">₹{pricing.price ?? '—'}</span>
                      {pricing.compareAtPrice !== null && (
                        <span className="text-[17px] text-gray-400 line-through">₹{pricing.compareAtPrice}</span>
                      )}
                      {pricing.discountPercentage && (
                        <span className="inline-flex items-center h-6 px-2.5 rounded-md bg-[#FFF1EC] text-[#D9531E] text-[12px] font-bold">
                          {pricing.discountPercentage}% OFF
                        </span>
                      )}
                    </div>
                    {/* The offer the backend applied to this price here, as it
                        returned it. Browse prices are indicative; the cart quote
                        confirms the final price. */}
                    {(pricing.offer || pricing.basketOffer) && (
                      <div
                        className="mt-3 rounded-xl border border-accent-100 bg-accent-50/60 px-4 py-2.5 text-[13px]"
                        data-testid="product-detail-offer"
                      >
                        {pricing.offer && (
                          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 font-semibold text-accent-700">
                            <Tag size={14} aria-hidden="true" />
                            <span>{pricing.offer.label}</span>
                            {pricing.savings !== null && (
                              <span className="font-medium text-emerald-800">You save {formatRupees(pricing.savings)}</span>
                            )}
                          </p>
                        )}
                        {pricing.offer && formatOfferDate(pricing.offer.endsAt) && (
                          <p className="mt-0.5 text-xs text-ink-secondary">Offer ends {formatOfferDate(pricing.offer.endsAt)}</p>
                        )}
                        {pricing.basketOffer && (
                          <p className={`text-emerald-800 ${pricing.offer ? 'mt-1.5' : ''}`}>
                            <span className="font-semibold">Multi-buy offer:</span> {pricing.basketOffer.label}
                          </p>
                        )}
                        <p className="mt-1 text-xs text-ink-secondary">Your final price is confirmed in your cart.</p>
                      </div>
                    )}

                    {/* Pack size and availability, from the projection. */}
                    <div className="mt-[14px] flex flex-wrap items-center gap-2.5">
                      {product.sellingUom && (
                        <span aria-label="Pack size" className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-lg border border-[#CFE3CF] bg-[#F1FAEF] text-emerald-950 text-[13px] font-bold">
                          <Scale size={14} className="text-emerald-700" aria-hidden="true" />
                          {product.sellingUom}
                        </span>
                      )}
                      {product.isInStock ? (
                        <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-emerald-700">
                          <span className="w-2 h-2 rounded-full bg-emerald-500" aria-hidden="true" />
                          In stock
                        </span>
                      ) : (
                        <span className="inline-flex items-center h-8 px-3 rounded-lg bg-[#FDF0EE] border border-[#F4D4D0] text-[#B42318] text-[13px] font-bold">
                          Out of stock
                        </span>
                      )}
                    </div>

                    {product.catchWeight && (
                      <p className="mt-3 text-amber-800 bg-amber-50 border border-amber-100 rounded-xl px-4 py-2.5 text-[13px]">
                        This item&apos;s weight may vary slightly. Catch-weight items are payable on delivery only.
                      </p>
                    )}

                    <div className="mt-[16px] flex flex-wrap items-center gap-3">
                      {product.isInStock ? (
                        quantity > 0 ? (
                          <>
                            <QuantityStepper size="lg" quantity={quantity} onChange={(qty) => setQuantity(product._id, qty)} />
                            <Link
                              to="/cart"
                              className={`inline-flex items-center justify-center gap-2 h-[46px] px-8 rounded-xl bg-[linear-gradient(90deg,#2E8B47,#1E7A3C)] text-white text-[15px] font-bold shadow-[0_8px_18px_-6px_rgba(46,139,71,0.55)] ${focusRing}`}
                            >
                              <ShoppingCart size={18} aria-hidden="true" />
                              View Cart
                            </Link>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={handleAddToCart}
                              className={`inline-flex items-center justify-center gap-2 h-[46px] px-8 rounded-xl bg-[linear-gradient(90deg,#2E8B47,#1E7A3C)] text-white text-[15px] font-bold shadow-[0_8px_18px_-6px_rgba(46,139,71,0.55)] hover:brightness-105 ${focusRing}`}
                            >
                              <ShoppingCart size={18} aria-hidden="true" />
                              Add to Cart
                            </button>
                            <button
                              type="button"
                              onClick={handleBuyNow}
                              className={`inline-flex items-center justify-center h-[46px] px-6 rounded-xl bg-white border border-[#2E8B47] text-[#1E5A30] text-[15px] font-bold hover:bg-emerald-50 ${focusRing}`}
                            >
                              Buy Now
                            </button>
                          </>
                        )
                      ) : (
                        <button
                          type="button"
                          disabled
                          className="inline-flex items-center justify-center gap-2 h-[46px] px-8 rounded-xl border border-[#E3E8E3] bg-[#F5F7F5] text-[#8A948D] font-semibold text-[15px] cursor-not-allowed"
                        >
                          <ShoppingCart size={18} aria-hidden="true" />
                          Currently unavailable
                        </button>
                      )}
                    </div>

                  </div>
                </div>

                {/* Brand promises in one row across the product area, so each
                    fits on a single line at every width. */}
                <ul className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-0 rounded-[18px] border border-[#E4EDE4] bg-white/70 px-4 py-3">
                  {BENEFITS.map(({ icon: Icon, title, subtitle }, index) => (
                    <li key={title} className={`relative flex items-center gap-3 sm:justify-center ${index > 0 ? 'sm:before:absolute sm:before:left-0 sm:before:top-1.5 sm:before:bottom-1.5 sm:before:w-px sm:before:bg-[#E9F1E9]' : ''}`}>
                      <span className="w-10 h-10 rounded-full bg-[#E4F3E5] text-emerald-700 flex items-center justify-center shrink-0">
                        <Icon size={18} aria-hidden="true" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[13px] font-bold text-emerald-950 leading-tight whitespace-nowrap">{title}</span>
                        <span className="block text-[12px] text-ink-secondary leading-tight mt-[2px] whitespace-nowrap">{subtitle}</span>
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Details panel — only what the projection carries. */}
                <section className={`mt-4 ${card} p-5 sm:p-6`} aria-labelledby="product-details-heading">
                  <h2 id="product-details-heading" className="serif text-[20px] font-bold text-emerald-950">Product Details</h2>
                  <p className="mt-2 text-[14px] text-[#4B5563] leading-[1.55]">
                    {product.longDescription || product.shortDescription || 'Sourced fresh, cleaned and hygienically packed before it reaches you.'}
                  </p>
                  {product.highlights?.length > 0 && (
                    <ul className="mt-3 space-y-1.5">
                      {product.highlights.map((highlight) => (
                        <li key={highlight} className="text-[14px] text-[#4B5563] flex items-start gap-2">
                          <Check size={15} className="text-emerald-700 mt-[3px] shrink-0" aria-hidden="true" />
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  )}
                  <ul className="mt-4 grid grid-cols-2 xl:grid-cols-4 gap-2.5">
                    <li className="flex items-center gap-2.5 rounded-xl bg-[#F6FAF4] border border-[#EAF2EA] px-3 py-2.5 min-w-0">
                      <span className="w-9 h-9 rounded-full bg-white text-emerald-700 flex items-center justify-center shrink-0 shadow-[0_2px_6px_-3px_rgba(15,51,24,0.3)]"><Scale size={17} aria-hidden="true" /></span>
                      <span className="min-w-0"><span className="block text-[12.5px] font-bold text-emerald-950">Pack size</span><span className="block text-[12px] text-ink-secondary mt-[1px] leading-snug">{product.sellingUom || 'As listed'}</span></span>
                    </li>
                    <li className="flex items-center gap-2.5 rounded-xl bg-[#F6FAF4] border border-[#EAF2EA] px-3 py-2.5 min-w-0">
                      <span className="w-9 h-9 rounded-full bg-white text-emerald-700 flex items-center justify-center shrink-0 shadow-[0_2px_6px_-3px_rgba(15,51,24,0.3)]"><Leaf size={17} aria-hidden="true" /></span>
                      <span className="min-w-0"><span className="block text-[12.5px] font-bold text-emerald-950">Category</span><span className="block text-[12px] text-ink-secondary mt-[1px] leading-snug">{product.categoryLabel || product.category}</span></span>
                    </li>
                    <li className="flex items-center gap-2.5 rounded-xl bg-[#F6FAF4] border border-[#EAF2EA] px-3 py-2.5 min-w-0">
                      <span className="w-9 h-9 rounded-full bg-white text-emerald-700 flex items-center justify-center shrink-0 shadow-[0_2px_6px_-3px_rgba(15,51,24,0.3)]"><Clock3 size={17} aria-hidden="true" /></span>
                      <span className="min-w-0"><span className="block text-[12.5px] font-bold text-emerald-950">Availability</span><span className="block text-[12px] text-ink-secondary mt-[1px] leading-snug">{product.isInStock ? 'In stock at your location' : 'Out of stock at your location'}</span></span>
                    </li>
                    <li className="flex items-center gap-2.5 rounded-xl bg-[#F6FAF4] border border-[#EAF2EA] px-3 py-2.5 min-w-0">
                      <span className="w-9 h-9 rounded-full bg-white text-emerald-700 flex items-center justify-center shrink-0 shadow-[0_2px_6px_-3px_rgba(15,51,24,0.3)]"><ShieldCheck size={17} aria-hidden="true" /></span>
                      <span className="min-w-0"><span className="block text-[12.5px] font-bold text-emerald-950">Packed</span><span className="block text-[12px] text-ink-secondary mt-[1px] leading-snug">Hygienically, before dispatch</span></span>
                    </li>
                  </ul>
                </section>
              </div>

              {/* Right column: delivery first on a phone, where it matters most. */}
              <aside className="flex flex-col gap-4 lg:col-start-2 lg:row-start-1 lg:row-span-2">
                <div className={`${card} p-5`}>
                  <h2 className="serif text-[18px] font-bold text-emerald-950 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-[#FFF1EC] text-[#E37B22] flex items-center justify-center" aria-hidden="true"><Leaf size={16} /></span>
                    Why Choose Fresh &amp; Homely?
                  </h2>
                  <ul className="mt-3.5 space-y-2.5">
                    {WHY_US.map((line) => (
                      <li key={line} className="flex items-center gap-2.5 text-[13.5px] text-[#374151]">
                        <span className="w-5 h-5 rounded-full bg-[#E4F3E5] text-emerald-700 flex items-center justify-center shrink-0"><Check size={12} strokeWidth={3} aria-hidden="true" /></span>
                        {line}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Delivery: the real browsing location and the real rule —
                    slots are offered per address at checkout. */}
                <div className={`${card} p-5 order-first lg:order-none`}>
                  <h2 className="serif text-[18px] font-bold text-emerald-950 flex items-center gap-2">
                    <Truck size={20} className="text-emerald-700" aria-hidden="true" />
                    Delivery Information
                  </h2>
                  <div className="mt-2 flex items-start justify-between gap-3">
                    <p className="text-[13px] text-[#4B5563]">
                      {locationStatus === 'set' ? `Delivering to ${displayLabelFor({ label: locationLabel, pincode: locationPincode })}` : 'Choose a delivery location to see availability'}
                    </p>
                    <button type="button" onClick={openLocationPrompt} className={`text-[12px] font-bold text-[#1E7A3C] shrink-0 ${focusRing}`}>
                      Change
                    </button>
                  </div>
                  <div className="mt-3.5 rounded-xl bg-[#F6FAF4] border border-[#EAF2EA] p-3 flex items-start gap-3">
                    <span className="w-9 h-9 rounded-full bg-[#E4F3E5] text-emerald-700 flex items-center justify-center shrink-0"><Clock3 size={17} aria-hidden="true" /></span>
                    <span className="text-[12.5px] text-[#4B5563] leading-[1.4]">
                      <span className="block text-[13px] font-bold text-emerald-950">Delivery slots</span>
                      Choose a delivery time for your address at checkout.
                    </span>
                  </div>
                  <div className="mt-2 rounded-xl bg-[#F6FAF4] border border-[#EAF2EA] p-3 flex items-start gap-3">
                    <span className="w-9 h-9 rounded-full bg-[#E4F3E5] text-emerald-700 flex items-center justify-center shrink-0"><MapPin size={17} aria-hidden="true" /></span>
                    <span className="text-[12.5px] text-[#4B5563] leading-[1.4]">
                      <span className="block text-[13px] font-bold text-emerald-950">Priced for your location</span>
                      Prices and stock shown are for the store that delivers to you.
                    </span>
                  </div>
                </div>

                <img src="/pages/pdp-promo.webp" alt="" aria-hidden="true" loading="lazy" width="322" height="140" className="hidden lg:block w-full h-auto rounded-2xl shadow-[0_6px_18px_-10px_rgba(15,51,24,0.25)]" />
              </aside>

              {(related.status === 'loading' || (related.status === 'success' && related.products.length > 0)) && (
                <section className="min-w-0 lg:col-start-1 lg:row-start-2 mt-2" aria-labelledby="related-heading">
                  <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
                    <div>
                      <h2 id="related-heading" className="serif text-[24px] sm:text-[26px] font-bold text-emerald-950 leading-tight">Related Products</h2>
                      <p className="text-[13.5px] text-[#4B5563] mt-0.5">More fresh options from the same category.</p>
                    </div>
                    <Link
                      to={`/categories/${product.category}`}
                      className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-[#D7E7D7] bg-white text-[13px] font-bold text-emerald-900 hover:bg-emerald-50 ${focusRing}`}
                    >
                      View All
                      <ArrowRight size={14} aria-hidden="true" />
                    </Link>
                  </div>
                  {/* The listing's own card and grid: normal card widths, so one
                      related product is neither stretched nor tiny. */}
                  {related.status === 'loading' && (
                    <ul className={`mt-4 ${CATALOG_GRID}`}>
                      {Array.from({ length: 4 }).map((_, index) => <CatalogTileSkeleton key={index} />)}
                    </ul>
                  )}
                  {related.status === 'success' && (
                    <ul className={`mt-4 ${CATALOG_GRID}`} data-testid="related-products">
                      {related.products.map((item) => (
                        <CatalogProductTile
                          key={item._id}
                          product={item}
                          view="grid"
                          quantity={quantityFor(item._id)}
                          favourited={isFavourite(item._id)}
                          onAddToCart={() => handleAddRelated(item)}
                          onSetQuantity={setQuantity}
                          onToggleFavourite={() => handleToggleRelatedFavourite(item)}
                        />
                      ))}
                    </ul>
                  )}
                </section>
              )}
            </div>
          )}

        </div>
      </main>
      <Footer />
    </>
  );
};

export default ProductDetailPage;
